import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, DollarSign, CreditCard, Hash, Calendar } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useCurrency } from '../../hooks/useCurrency';
import { shopService, Shop } from '../../services/shopService';
import { customToast } from '../../utils/customToast';
import { format, addMonths } from 'date-fns';

interface RecordPaymentModalProps {
  isOpen: boolean;
  shop: Shop | null;
  onClose: () => void;
  onSuccess: () => void;
}

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({ isOpen, shop, onClose, onSuccess }) => {
  const { theme } = useTheme();
  const { currencySymbol } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [formData, setFormData] = useState({
    amount: 0,
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: 'cash',
    reference: '',
    subscription_months: 12,
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Calculate subscription period
  const subscriptionPeriod = useMemo(() => {
    if (!shop) return null;
    
    const paymentDate = new Date(formData.payment_date);
    
    // Always start subscription from payment date for new payments
    // This allows renewing/replacing existing subscriptions
    const startDate = paymentDate;
    
    // Calculate end date: start date + subscription months
    // Example: Payment on Oct 10, 2025 + 12 months = Subscription valid until Oct 10, 2026
    // Next payment due on Oct 11, 2026
    const endDate = addMonths(new Date(startDate), formData.subscription_months);
    
    const nextPaymentDate = new Date(endDate);
    nextPaymentDate.setDate(nextPaymentDate.getDate() + 1); // Next payment is the day after subscription ends
    
    return {
      startDate,
      endDate,
      nextPaymentDate,
      isExtension: false, // Always starting fresh from payment date
    };
  }, [shop, formData.payment_date, formData.subscription_months]);

  if (!isOpen || !shop) return null;

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }
    if (!formData.payment_date) {
      newErrors.payment_date = 'Payment date is required';
    }
    if (!formData.payment_method) {
      newErrors.payment_method = 'Payment method is required';
    }
    if (!formData.subscription_months || formData.subscription_months < 1) {
      newErrors.subscription_months = 'Subscription months must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      customToast.error('Please fix the validation errors');
      return;
    }

    try {
      setLoading(true);
      await shopService.recordPayment(shop.id, formData);
      customToast.success('Payment recorded successfully');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error recording payment:', error);
      customToast.error(error.response?.data?.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setFormData({
        amount: 0,
        payment_date: format(new Date(), 'yyyy-MM-dd'),
        payment_method: 'cash',
        reference: '',
        subscription_months: 12,
        notes: '',
      });
      setErrors({});
      onClose();
    }, 150);
  };

  if (!isOpen || !shop) return null;

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50 ${
        isClosing ? 'animate-fadeOut' : 'animate-fadeIn'
      }`}
      style={{ touchAction: 'manipulation' }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div 
        className={`w-full max-w-4xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${
          isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
        } ${theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b rounded-t-xl flex-shrink-0 ${
          theme === 'dark' ? 'border-gray-700 bg-slate-800/95' : 'border-gray-200 bg-gray-50/95'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              <div className={`p-2 rounded-xl flex-shrink-0 ${
                theme === 'dark' ? 'bg-green-500/10' : 'bg-green-50'
              }`}>
                <DollarSign className={`w-5 h-5 ${
                  theme === 'dark' ? 'text-green-400' : 'text-green-600'
                }`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-base sm:text-lg font-semibold truncate ${
                  theme === 'dark' ? 'text-white' : 'text-slate-900'
                }`}>
                  Record Payment
                </h2>
                <p className={`text-xs sm:text-sm truncate ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Process payment and extend subscription
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className={`p-2 rounded-xl transition-all duration-200 touch-manipulation flex-shrink-0 ${
                theme === 'dark' 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-slate-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} id="record-payment-form" className="flex-1 overflow-y-auto">
          <div className="px-4 sm:px-5 md:px-6 py-4 sm:py-5">
            {/* Active Subscription Info */}
            {shop.payment_status === 'paid' && shop.subscription_ends_at && new Date(shop.subscription_ends_at) > new Date() && (
              <div className={`mb-4 sm:mb-5 p-4 rounded-xl border-2 ${
                theme === 'dark' ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-start space-x-3">
                  <div className={`flex-shrink-0 p-2 rounded-lg ${
                    theme === 'dark' ? 'bg-blue-500/10' : 'bg-blue-100'
                  }`}>
                    <Calendar className={`w-5 h-5 ${
                      theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`text-sm font-semibold mb-1 ${
                      theme === 'dark' ? 'text-blue-300' : 'text-blue-900'
                    }`}>
                      Active Subscription
                    </h4>
                    <p className={`text-xs sm:text-sm mb-2 ${
                      theme === 'dark' ? 'text-blue-200' : 'text-blue-800'
                    }`}>
                      This shop has already paid for the current subscription period.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
                      <div>
                        <span className={`font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          Current Period Ends:
                        </span>
                        <span className={`ml-2 font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {format(new Date(shop.subscription_ends_at), 'MMM dd, yyyy')}
                        </span>
                      </div>
                      <div>
                        <span className={`font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          Payment Status:
                        </span>
                        <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          theme === 'dark' ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'
                        }`}>
                          Paid
                        </span>
                      </div>
                    </div>
                    <p className={`text-xs mt-2 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      💡 New payment will extend subscription starting {format(
                        new Date(new Date(shop.subscription_ends_at).getTime() + 24 * 60 * 60 * 1000), 
                        'MMM dd, yyyy'
                      )} (day after current period ends).
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4 sm:space-y-5">
              {/* Row 1: Amount and Payment Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {/* Amount */}
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Amount ({currencySymbol}) *
                  </label>
                  <div className="relative">
                    <DollarSign className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount || ''}
                      onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                      className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-3 sm:py-2.5 text-sm sm:text-base border rounded-xl transition-colors touch-manipulation ${
                        errors.amount
                          ? 'border-red-500 focus:ring-red-500'
                          : theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                      } focus:outline-none`}
                      placeholder="0.00"
                    />
                  </div>
                  {errors.amount && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.amount}</p>
                  )}
                </div>

                {/* Payment Date */}
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Payment Date *
                  </label>
                  <div className="relative">
                    <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                    <input
                      type="date"
                      value={formData.payment_date}
                      onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                      className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-3 sm:py-2.5 text-sm sm:text-base border rounded-xl transition-colors touch-manipulation ${
                        errors.payment_date
                          ? 'border-red-500 focus:ring-red-500'
                          : theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                      } focus:outline-none`}
                    />
                  </div>
                  {errors.payment_date && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.payment_date}</p>
                  )}
                </div>
              </div>

              {/* Row 2: Payment Method and Subscription Months */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {/* Payment Method */}
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Payment Method *
                  </label>
                  <div className="relative">
                    <CreditCard className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                    <select
                      value={formData.payment_method}
                      onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                      className={`w-full pl-9 sm:pl-10 pr-4 py-3 sm:py-2.5 rounded-xl border text-sm sm:text-base touch-manipulation ${
                        errors.payment_method
                          ? 'border-red-500 focus:ring-red-500'
                          : theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                      } focus:outline-none focus:ring-2`}
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="credit_card">Credit Card</option>
                      <option value="debit_card">Debit Card</option>
                      <option value="cheque">Cheque</option>
                      <option value="online">Online Payment</option>
                    </select>
                  </div>
                  {errors.payment_method && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.payment_method}</p>
                  )}
                </div>

                {/* Subscription Months */}
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Subscription Period (Months) *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={formData.subscription_months}
                    onChange={(e) => setFormData({ ...formData, subscription_months: parseInt(e.target.value) || 0 })}
                    className={`w-full px-4 py-3 sm:py-2.5 rounded-xl border text-sm sm:text-base touch-manipulation ${
                      errors.subscription_months
                        ? 'border-red-500 focus:ring-red-500'
                        : theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                    } focus:outline-none focus:ring-2`}
                    placeholder="12"
                  />
                  {errors.subscription_months && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.subscription_months}</p>
                  )}
                </div>
              </div>

              {/* Row 3: Payment Reference (Full Width) */}
              <div>
                <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Payment Reference/Transaction ID
                </label>
                <div className="relative">
                  <Hash className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                  <input
                    type="text"
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                    className={`w-full pl-9 sm:pl-10 pr-4 py-3 sm:py-2.5 rounded-xl border text-sm sm:text-base touch-manipulation ${
                      errors.reference
                        ? 'border-red-500 focus:ring-red-500'
                        : theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                    } focus:outline-none focus:ring-2`}
                    placeholder="TXN-123456789"
                  />
                </div>
                {errors.reference && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.reference}</p>
                )}
              </div>

              {/* Subscription Period Summary */}
              {subscriptionPeriod && (
                <div className={`p-4 rounded-xl border-2 ${
                  theme === 'dark' ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'
                }`}>
                  <h4 className={`text-sm font-semibold mb-3 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-900'}`}>
                    Subscription Period Calculation
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className={`font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Starts</p>
                      <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} font-semibold`}>
                        {format(subscriptionPeriod.startDate, 'MMM dd, yyyy')}
                      </p>
                      {subscriptionPeriod.isExtension && (
                        <span className={`inline-block mt-2 text-xs px-2 py-1 rounded-lg ${
                          theme === 'dark' ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'
                        }`}>
                          Extension
                        </span>
                      )}
                    </div>
                    <div>
                      <p className={`font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Ends</p>
                      <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} font-semibold`}>
                        {format(subscriptionPeriod.endDate, 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div>
                      <p className={`font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Duration</p>
                      <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} font-semibold`}>
                        {formData.subscription_months} {formData.subscription_months === 1 ? 'month' : 'months'}
                      </p>
                    </div>
                    <div>
                      <p className={`font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Next Payment Due</p>
                      <p className={`${theme === 'dark' ? 'text-orange-300' : 'text-orange-700'} font-semibold`}>
                        {format(subscriptionPeriod.nextPaymentDate, 'MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className={`w-full px-4 py-3 sm:py-2.5 rounded-xl border text-sm sm:text-base touch-manipulation resize-none ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                  } focus:outline-none focus:ring-2`}
                  placeholder="Any additional notes about this payment..."
                />
              </div>
            </div>
          </div>
        </form>

          {/* Footer */}
          <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t rounded-b-xl flex-shrink-0 ${
            theme === 'dark' ? 'border-gray-700 bg-slate-800/95' : 'border-gray-200 bg-gray-50/95'
          }`}>
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className={`px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-xl border transition-all duration-200 shadow-lg hover:shadow-xl touch-manipulation ${
                theme === 'dark'
                  ? 'border-gray-600 text-gray-300 hover:bg-slate-700 hover:border-gray-500'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="record-payment-form"
              disabled={loading}
              className={`px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl touch-manipulation ${
                theme === 'dark'
                  ? 'bg-blue-500 hover:bg-blue-600 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              <span>{loading ? 'Recording...' : 'Record Payment'}</span>
            </button>
            </div>
          </div>
      </div>
    </div>,
    document.body
  );
};

export default RecordPaymentModal;
